import { c as _c } from "react/compiler-runtime";
import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { ArrowRight } from '@/components/icons/ArrowRight';
import { Google as GoogleIcon } from '@/components/icons/social/Google';
import { cn } from '@/lib/styles';
import { Spinner } from '../elementary/Spinner';
const buttonVariants = cva('inline-flex items-center justify-center disabled:pointer-events-none disabled:bg-wolf-gray-200 disabled:text-wolf-gray-500 disabled:opacity-100 transition-[color,background-color,border-color,box-shadow] duration-200 ease-in-out cursor-pointer', {
  variants: {
    size: {
      large: 'h-13 px-6 py-3 lok-text-body-bold-large',
      medium: 'h-11 px-5 py-2.5 lok-text-body-bold-medium',
      small: 'h-10.5 px-5 lok-text-body-bold-medium',
      pillow: 'px-3 py-0.75',
      switch: 'px-6 py-1.5',
      none: 'p-0'
    },
    variant: {
      primary: 'rounded-button bg-wolf-gray-900 hover:bg-wolf-gray-700 active:bg-wolf-gray-800 text-wolf-gray-25 leading-1 text-center sm:text-left',
      secondary: 'rounded-button border border-wolf-gray-500 outline-2 outline-transparent -outline-offset-2 transition-[color,background-color,border-color,outline-color] duration-200 text-wolf-gray-950 hover:border-wolf-gray-950 hover:outline-wolf-gray-500 focus-visible:border-wolf-gray-950 focus-visible:outline-wolf-gray-500 active:border-wolf-gray-950 active:outline-wolf-gray-500 disabled:border-wolf-gray-300 disabled:text-wolf-gray-400 disabled:bg-transparent leading-1 text-center sm:text-left',
      google: 'rounded-button overflow-hidden p-0 disabled:bg-transparent disabled:text-white disabled:opacity-100',
      pillowLarge: 'rounded-full border border-neutral-light-alpha-500 hover:border-wolf-gray-900 bg-transparent text-wolf-gray-900 hover:text-wolf-gray-700 hover:bg-neutral-dark-alpha-500 active:bg-wolf-gray-900 active:text-wolf-gray-25 active:border-wolf-gray-900 lok-text-body-bold-medium',
      pillowSmall: 'rounded-full border border-neutral-light-alpha-500 hover:border-wolf-gray-900 bg-transparent text-wolf-gray-900 hover:text-wolf-gray-700 hover:bg-neutral-dark-alpha-500 text-sm/2 lg:text-base/3 active:bg-wolf-gray-900 active:text-wolf-gray-25 active:border-wolf-gray-900',
      switch: 'rounded-button border border-neutral-light-alpha-500 hover:border-wolf-gray-900 bg-transparent text-wolf-gray-900 hover:text-wolf-gray-700 hover:bg-neutral-dark-alpha-500 active:bg-wolf-gray-900 active:text-wolf-gray-25 active:border-wolf-gray-900 lok-text-body-bold-small',
      primaryLink: 'text-brand-black rounded-none  font-bold text-lg/4 lg:text-3xl/11 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
      secondaryLink: 'text-neutral-dark-300  rounded-none font-bold lg:text-2xl/11 text-base/4 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
      tertiaryLink: 'text-neutral-dark-300 rounded-none font-normal underline text-2xl/9 lg:text-2xl/7 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
      quaternaryLink: 'text-neutral-dark-300 rounded-none font-normal underline text-lg/5 lg:text-lg/7 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
      none: '',
      ghost: 'hover:text-brand-salmon duration-200 transition-colors'
    },
    inverted: {
      true: '',
      false: ''
    }
  },
  compoundVariants: [{
    variant: 'primary',
    inverted: true,
    className: 'bg-wolf-gray-25 text-wolf-gray-950 hover:bg-wolf-gray-100 active:bg-wolf-gray-200 disabled:bg-wolf-gray-600 disabled:text-wolf-gray-400 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-wolf-gray-900'
  }, {
    variant: 'secondary',
    inverted: true,
    className: 'border-wolf-gray-25 bg-transparent text-wolf-gray-25 hover:bg-wolf-gray-800 active:bg-wolf-gray-700 disabled:border-wolf-gray-600 disabled:text-wolf-gray-500 disabled:bg-transparent'
  }],
  defaultVariants: {
    variant: 'primary',
    size: 'large',
    inverted: false
  }
});
interface ButtonBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}
interface ButtonSlotProps extends ButtonBaseProps {
  asChild: true;
  isLoading?: never;
  iconStart?: never;
  iconEnd?: never;
}
interface ButtonStandardProps extends ButtonBaseProps {
  asChild?: false;
  isLoading?: boolean;
  iconStart?: React.ReactNode;
  iconEnd?: boolean;
}
export type ButtonProps = ButtonSlotProps | ButtonStandardProps;
export function OAuthSplitLayout(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="flex items-center justify-center aspect-square h-full shrink-0 rounded-l-button bg-white border border-fjord-blue-600 group-hover/oauth:border-fjord-blue-700 group-active/oauth:border-fjord-blue-800 group-disabled/oauth:border-fjord-blue-300 transition-colors duration-200"><GoogleIcon className="size-6 group-disabled/oauth:opacity-50" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <span className="flex items-center h-full w-full">{t1}<span className="flex flex-1 items-center justify-center rounded-r-button bg-fjord-blue-600 group-hover/oauth:bg-fjord-blue-700 group-active/oauth:bg-fjord-blue-800 group-disabled/oauth:bg-fjord-blue-300 px-6 h-full text-white lok-text-body-bold-large transition-colors duration-200">{children}</span></span>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function IconLayout(t0) {
  const $ = _c(10);
  const {
    iconStart,
    iconEnd,
    size,
    children
  } = t0;
  const isLarge = size === "large";
  let t1;
  if ($[0] !== iconStart || $[1] !== isLarge) {
    t1 = iconStart && <span className={cn("shrink-0 mr-2", isLarge ? "size-6" : "size-5")}>{iconStart}</span>;
    $[0] = iconStart;
    $[1] = isLarge;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== iconEnd || $[4] !== isLarge) {
    t2 = iconEnd && <span className={cn("shrink-0 ml-1 transition-transform duration-200 group-hover/icon-end:translate-x-1", isLarge ? "size-5" : "size-4")}><ArrowRight className="size-full" /></span>;
    $[3] = iconEnd;
    $[4] = isLarge;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children || $[7] !== t1 || $[8] !== t2) {
    t3 = <>{t1}{children}{t2}</>;
    $[6] = children;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((t0, ref) => {
  const $ = _c(41);
  let className;
  let disabled;
  let iconEnd;
  let iconStart;
  let inverted;
  let isLoading;
  let props;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      inverted,
      asChild: t1,
      isLoading,
      iconStart,
      iconEnd,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = disabled;
    $[3] = iconEnd;
    $[4] = iconStart;
    $[5] = inverted;
    $[6] = isLoading;
    $[7] = props;
    $[8] = size;
    $[9] = t1;
    $[10] = variant;
  } else {
    className = $[1];
    disabled = $[2];
    iconEnd = $[3];
    iconStart = $[4];
    inverted = $[5];
    isLoading = $[6];
    props = $[7];
    size = $[8];
    t1 = $[9];
    variant = $[10];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  const isOAuth = variant === "google";
  const isDisabled = disabled || isLoading;
  let t2;
  if ($[11] !== isLoading) {
    t2 = isLoading && {
      "aria-busy": true
    };
    $[11] = isLoading;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== asChild || $[14] !== isDisabled) {
    t3 = asChild && isDisabled && {
      "aria-disabled": true
    };
    $[13] = asChild;
    $[14] = isDisabled;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== t2 || $[17] !== t3) {
    t4 = {
      ...t2,
      ...t3
    };
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const ariaProps = t4;
  let t5;
  if ($[19] !== className || $[20] !== iconEnd || $[21] !== inverted || $[22] !== isOAuth || $[23] !== size || $[24] !== variant) {
    t5 = cn(buttonVariants({
      variant,
      size,
      inverted,
      className
    }), isOAuth && "group/oauth", iconEnd && "group/icon-end");
    $[19] = className;
    $[20] = iconEnd;
    $[21] = inverted;
    $[22] = isOAuth;
    $[23] = size;
    $[24] = variant;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const t6 = !asChild && isDisabled;
  let t7;
  if ($[26] !== iconEnd || $[27] !== iconStart || $[28] !== isLoading || $[29] !== isOAuth || $[30] !== props.children || $[31] !== size) {
    t7 = isLoading && !isOAuth ? <Spinner>{props.children}</Spinner> : isOAuth ? <OAuthSplitLayout>{props.children}</OAuthSplitLayout> : iconStart || iconEnd ? <IconLayout iconStart={iconStart} iconEnd={iconEnd} size={size}>{props.children}</IconLayout> : props.children;
    $[26] = iconEnd;
    $[27] = iconStart;
    $[28] = isLoading;
    $[29] = isOAuth;
    $[30] = props.children;
    $[31] = size;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== Comp || $[34] !== ariaProps || $[35] !== props || $[36] !== ref || $[37] !== t5 || $[38] !== t6 || $[39] !== t7) {
    t8 = <Comp className={t5} ref={ref} disabled={t6} {...ariaProps} {...props}>{t7}</Comp>;
    $[33] = Comp;
    $[34] = ariaProps;
    $[35] = props;
    $[36] = ref;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  return t8;
});
Button.displayName = 'Button';
export { Button, buttonVariants };